import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiUsers, 
  FiDownload, 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiCalendar, 
  FiTrendingUp, 
  FiDollarSign,
  FiFilter,
  FiSearch,
  FiExternalLink,
  FiEye,
  FiX,
  FiHome,
  FiUser
} = FiIcons;

const AdminDashboard = ({ downloads, onClose }) => {
  const [filteredDownloads, setFilteredDownloads] = useState(downloads);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    let filtered = downloads;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(download => 
        download.restaurant.toLowerCase().includes(searchTerm.toLowerCase()) ||
        download.owner.toLowerCase().includes(searchTerm.toLowerCase()) ||
        download.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }
      
      filtered = filtered.filter(download => {
        const downloadDate = new Date(download.timestamp);
        return downloadDate >= filterDate;
      });
    }

    setFilteredDownloads(filtered);
  }, [downloads, searchTerm, dateFilter]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getLeadQuality = (profit, margin) => {
    const profitValue = parseFloat(profit.replace(/[^\d.-]/g, ''));
    const marginValue = parseFloat(margin.replace('%', ''));
    
    if (profitValue > 1000 && marginValue > 15) return { label: 'High', color: 'bg-green-100 text-green-800' };
    if (profitValue > 500 && marginValue > 10) return { label: 'Medium', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Low', color: 'bg-red-100 text-red-800' };
  };

  const totalLeads = downloads.length;
  const totalRevenue = downloads.reduce((sum, download) => {
    return sum + parseFloat(download.revenue.replace(/[^\d.-]/g, ''));
  }, 0);
  const avgProfit = downloads.length > 0 ? downloads.reduce((sum, download) => {
    return sum + parseFloat(download.profit.replace(/[^\d.-]/g, ''));
  }, 0) / downloads.length : 0;

  const exportToCSV = () => {
    const csvContent = [
      ['Restaurant', 'Owner', 'Email', 'Phone', 'Location', 'Revenue', 'Profit', 'Margin', 'Date'],
      ...filteredDownloads.map(download => [
        download.restaurant,
        download.owner,
        download.email,
        download.phone,
        download.location,
        download.revenue,
        download.profit,
        download.margin,
        download.timestamp
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `restaurant_leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiUsers} className="text-2xl" />
              <div>
                <h2 className="text-2xl font-bold">Admin Dashboard</h2>
                <p className="text-blue-100">Lead Generation & Analytics</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="p-6 border-b border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200">
              <div className="flex items-center gap-3">
                <div className="bg-green-500 p-3 rounded-xl">
                  <SafeIcon icon={FiUsers} className="text-white text-xl" />
                </div>
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Leads</p>
                  <p className="text-2xl font-bold text-green-800">{totalLeads}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 p-3 rounded-xl">
                  <SafeIcon icon={FiDollarSign} className="text-white text-xl" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Revenue</p>
                  <p className="text-2xl font-bold text-blue-800">{formatCurrency(totalRevenue)}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-200">
              <div className="flex items-center gap-3">
                <div className="bg-purple-500 p-3 rounded-xl">
                  <SafeIcon icon={FiTrendingUp} className="text-white text-xl" />
                </div>
                <div>
                  <p className="text-sm text-purple-600 font-medium">Avg Daily Profit</p>
                  <p className="text-2xl font-bold text-purple-800">{formatCurrency(avgProfit)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-4 items-center">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search restaurants, owners, or emails..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                />
              </div>
              
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last Week</option>
                <option value="month">Last Month</option>
              </select>
            </div>

            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiDownload} className="text-sm" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Leads Table */}
        <div className="p-6 overflow-auto max-h-96">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Restaurant</th>
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Owner</th>
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Contact</th>
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Revenue</th>
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Profit</th>
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Quality</th>
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Date</th>
                  <th className="text-left p-4 font-semibold text-slate-700 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDownloads.map((download) => {
                  const quality = getLeadQuality(download.profit, download.margin);
                  return (
                    <tr key={download.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4 border-b">
                        <div>
                          <p className="font-medium text-slate-800">{download.restaurant}</p>
                          <p className="text-sm text-slate-500">{download.location}</p>
                        </div>
                      </td>
                      <td className="p-4 border-b">
                        <p className="font-medium text-slate-800">{download.owner}</p>
                      </td>
                      <td className="p-4 border-b">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiMail} className="text-slate-400 text-sm" />
                            <span className="text-sm text-slate-600">{download.email}</span>
                          </div>
                          {download.phone && (
                            <div className="flex items-center gap-2">
                              <SafeIcon icon={FiPhone} className="text-slate-400 text-sm" />
                              <span className="text-sm text-slate-600">{download.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4 border-b">
                        <p className="font-medium text-slate-800">{download.revenue}</p>
                      </td>
                      <td className="p-4 border-b">
                        <div>
                          <p className="font-medium text-slate-800">{download.profit}</p>
                          <p className="text-sm text-slate-500">{download.margin}</p>
                        </div>
                      </td>
                      <td className="p-4 border-b">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${quality.color}`}>
                          {quality.label}
                        </span>
                      </td>
                      <td className="p-4 border-b">
                        <p className="text-sm text-slate-600">{download.timestamp}</p>
                      </td>
                      <td className="p-4 border-b">
                        <button
                          onClick={() => setSelectedLead(download)}
                          className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          <SafeIcon icon={FiEye} className="text-sm" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredDownloads.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <SafeIcon icon={FiUsers} className="text-4xl mx-auto mb-2 text-slate-300" />
              <p>No leads found matching your criteria</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-auto"
          >
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">Lead Details</h3>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-slate-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3">Restaurant Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiHome} className="text-slate-400" />
                      <div>
                        <p className="font-medium">{selectedLead.restaurant}</p>
                        <p className="text-sm text-slate-500">Restaurant Name</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiMapPin} className="text-slate-400" />
                      <div>
                        <p className="font-medium">{selectedLead.location || 'Not provided'}</p>
                        <p className="text-sm text-slate-500">Location</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-700 mb-3">Owner Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiUser} className="text-slate-400" />
                      <div>
                        <p className="font-medium">{selectedLead.owner}</p>
                        <p className="text-sm text-slate-500">Owner Name</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiMail} className="text-slate-400" />
                      <div>
                        <p className="font-medium">{selectedLead.email}</p>
                        <p className="text-sm text-slate-500">Email Address</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiPhone} className="text-slate-400" />
                      <div>
                        <p className="font-medium">{selectedLead.phone || 'Not provided'}</p>
                        <p className="text-sm text-slate-500">Phone Number</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-slate-700 mb-3">Financial Performance</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-sm text-blue-600 font-medium">Daily Revenue</p>
                    <p className="text-xl font-bold text-blue-800">{selectedLead.revenue}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl">
                    <p className="text-sm text-green-600 font-medium">Daily Profit</p>
                    <p className="text-xl font-bold text-green-800">{selectedLead.profit}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-xl">
                    <p className="text-sm text-purple-600 font-medium">Profit Margin</p>
                    <p className="text-xl font-bold text-purple-800">{selectedLead.margin}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-slate-700 mb-3">Lead Quality</h4>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLeadQuality(selectedLead.profit, selectedLead.margin).color}`}>
                    {getLeadQuality(selectedLead.profit, selectedLead.margin).label} Quality Lead
                  </span>
                  <span className="text-sm text-slate-500">Downloaded on {selectedLead.timestamp}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <a
                  href={`mailto:${selectedLead.email}?subject=Local Trust Guide - Restaurant Consultation&body=Hi ${selectedLead.owner},%0D%0A%0D%0AI hope this email finds you well. I noticed you recently used our Restaurant Profit Calculator for ${selectedLead.restaurant}.%0D%0A%0D%0ABest regards`}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiMail} className="text-sm" />
                  Send Email
                </a>
                {selectedLead.phone && (
                  <a
                    href={`tel:${selectedLead.phone}`}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <SafeIcon icon={FiPhone} className="text-sm" />
                    Call
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AdminDashboard;